/**
 * EditableKanbanColumn Component
 *
 * Wraps the original KanbanColumn component with editing capabilities for Create Board form.
 * Achieves true WYSIWYG by reusing the actual KanbanColumn instead of duplicating code.
 *
 * Architecture:
 * - Reuses KanbanColumn directly (not a copy)
 * - Adds EditControlsOverlay on hover for editing
 * - Uses real KanbanCard components via mock data
 * - Uses CollapsedColumn for collapsed state (via KanbanColumn)
 *
 * Benefits:
 * - Visual parity guaranteed (it IS the real component)
 * - Automatic updates when KanbanColumn changes
 * - No duplicate code to maintain
 */

import React, { useState, useRef, useEffect } from 'react';
import KanbanColumn from '@/components/core/Kanban/KanbanColumn';
import { EditControlsOverlay } from './EditControlsOverlay';
import { CreateBoardColumnsItem } from '@/types/generated/processor-types';
import { createMockTasks } from '@/utils/mock-tasks';
import { layout } from '@/styles/design-tokens';

interface EditableKanbanColumnProps {
  column: CreateBoardColumnsItem;
  index: number;
  totalColumns: number;
  onUpdate: (index: number, column: CreateBoardColumnsItem) => void;
  onRemove: (index: number) => void;
  onDragStart?: (index: number) => void;
  onDragOver?: (index: number) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
}

export const EditableKanbanColumn: React.FC<EditableKanbanColumnProps> = ({
  column,
  index,
  totalColumns,
  onUpdate,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragging,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showEditControls, setShowEditControls] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showEditControls) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowEditControls(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEditControls(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showEditControls]);

  // Generate mock cards using the utility
  // This ensures we use real KanbanCard components, not hardcoded divs
  const mockCards = createMockTasks(3, column.name);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    onDragStart?.(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver?.(index);
  };

  return (
    <div
      className="relative flex-shrink-0 h-full"
      style={{
        width: isCollapsed
          ? layout.kanban.columnCollapsed
          : layout.kanban.columnWidth,
        opacity: isDragging ? 0.5 : 1,
        transition: 'opacity 0.2s ease, width 0.3s ease',
      }}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={onDragEnd}
      ref={wrapperRef}
      onMouseEnter={() => setShowEditControls(true)}
    >
      {/* Edit Controls Overlay - Only visible on hover when expanded */}
      {showEditControls && !isDragging && !isCollapsed && (
        <EditControlsOverlay
          column={column}
          index={index}
          totalColumns={totalColumns}
          onUpdate={onUpdate}
          onRemove={onRemove}
        />
      )}

      {/* Original KanbanColumn - Reused directly for WYSIWYG */}
      <KanbanColumn
        id={`preview-${index}`}
        name={column.name}
        color={column.color}
        cards={mockCards}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        // Disable card drag-and-drop (not needed for preview)
        onCardDragStart={undefined}
        onCardDragEnd={undefined}
        // Disable card clicks (not needed for preview)
        onCardClick={undefined}
        // Column drag-and-drop is handled at this wrapper level
        onDragOver={undefined}
        onDrop={undefined}
      />
    </div>
  );
};
